import React, { useState, useEffect } from 'react';
import { getPrefillDefaults, setPrefillDefaults } from '../lib/fax-api';

// Practice-level PREFILL DEFAULTS editor for faxed forms. The provider/office values
// used to pre-fill forms are synced from Elation; when Elation has a junk value on
// file (e.g. a "Student…" specialty) an admin overrides just that item here, and the
// rest keep following Elation. Overrides are SPARSE — we only store fields whose value
// differs from the Elation-synced one.
const FIELDS = [
  { bucket: 'provider', key: 'name', label: 'Provider Name' },
  { bucket: 'provider', key: 'npi', label: 'NPI #' },
  { bucket: 'provider', key: 'specialty', label: 'Specialty' },
  { bucket: 'office', key: 'street', label: 'Office Street Address' },
  { bucket: 'office', key: 'city', label: 'Office City' },
  { bucket: 'office', key: 'state', label: 'Office State' },
  { bucket: 'office', key: 'zip', label: 'Office Zip' },
  { bucket: 'office', key: 'phone', label: 'Office Phone' },
  { bucket: 'office', key: 'fax', label: 'Office Fax' },
];
const fid = (f) => `${f.bucket}.${f.key}`;

export function FaxPrefillDefaultsPanel({ onChange }) {
  const [data, setData] = useState(null);     // { synced, overrides, can_edit }
  const [draft, setDraft] = useState({});      // fid -> string (effective value in the box)
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [ok, setOk] = useState(false);
  const [collapsed, setCollapsed] = useState(true);

  function seed(d) {
    const next = {};
    for (const f of FIELDS) {
      const ov = (d.overrides && d.overrides[f.bucket]) || {};
      const sy = (d.synced && d.synced[f.bucket]) || {};
      next[fid(f)] = ov[f.key] != null && ov[f.key] !== '' ? String(ov[f.key]) : String(sy[f.key] || '');
    }
    setDraft(next);
  }

  useEffect(() => {
    getPrefillDefaults().then((d) => { setData(d); seed(d); }).catch((e) => setError(e.message));
  }, []);

  if (error && !data) return <div className="fax-mode-error">Error: {error}</div>;
  if (!data) return null;

  const canEdit = !!data.can_edit;
  const syncedVal = (f) => String(((data.synced && data.synced[f.bucket]) || {})[f.key] || '');
  const overriddenCount = FIELDS.filter((f) => { const v = (draft[fid(f)] || '').trim(); return v && v !== syncedVal(f); }).length;

  function setField(id, value) { setOk(false); setDraft((prev) => ({ ...prev, [id]: value })); }

  function buildOverrides() {
    const ov = {};
    for (const f of FIELDS) {
      const v = (draft[fid(f)] || '').trim();
      if (v && v !== syncedVal(f)) (ov[f.bucket] = ov[f.bucket] || {})[f.key] = v;
    }
    return ov;
  }

  async function save() {
    if (!canEdit || busy) return;
    setBusy(true); setError(null); setOk(false);
    try {
      const r = await setPrefillDefaults(buildOverrides());
      const next = { ...data, overrides: r.overrides || {} };
      setData(next); seed(next); setOk(true);
      if (onChange) onChange();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  const renderBucket = (bucket, title) => (
    <div className="fax-automation-row" key={bucket}>
      <div className="settings-row-label" style={{ marginBottom: 8 }}>{title}</div>
      {FIELDS.filter((f) => f.bucket === bucket).map((f) => {
        const id = fid(f);
        const val = draft[id] || '';
        const syn = syncedVal(f);
        const isOverride = !!val.trim() && val.trim() !== syn;
        return (
          <div className="settings-row" key={id}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="settings-row-label">
                {f.label}
                {isOverride && <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--accent, #2563eb)' }}>overridden</span>}
              </div>
              <div className="settings-row-desc">Elation: {syn || '— none on file —'}</div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
              <input
                type="text" value={val} disabled={!canEdit || busy}
                onChange={(e) => setField(id, e.target.value)} placeholder={syn || '—'}
                style={{ minWidth: 220 }} aria-label={f.label}
              />
              {isOverride && canEdit && (
                <button type="button" className="btn ghost sm" disabled={busy} onClick={() => setField(id, syn)}>Revert</button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="fax-automation-toggles">
      <button
        type="button" className="fax-automation-title" aria-expanded={!collapsed}
        onClick={() => setCollapsed((c) => !c)}
        style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', background: 'none', border: 0, padding: 0, margin: 0, cursor: 'pointer' }}
      >
        <span aria-hidden="true" style={{ fontSize: 11, lineHeight: 1 }}>{collapsed ? '▸' : '▾'}</span>
        Prefill defaults (provider &amp; office)
        <span style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: 'var(--ink-3)' }}>
          {overriddenCount ? `${overriddenCount} overridden` : 'all from Elation'}
        </span>
      </button>
      {!collapsed && (
        <div role="group" aria-label="Fax prefill defaults">
          <div className="settings-row-desc" style={{ margin: '4px 0 10px' }}>
            These auto-fill the provider/office fields on faxed forms. Values come from Elation; change any item to override it (everything else keeps following Elation).
          </div>
          {renderBucket('provider', 'Provider')}
          {renderBucket('office', 'Office')}
          {!canEdit && <div className="fax-automation-readonly-note">Read-only — ask an admin to change prefill defaults.</div>}
          {canEdit && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12 }}>
              <button type="button" className="btn accent sm" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save overrides'}</button>
              {ok && <span style={{ fontSize: 12, color: 'var(--ok, #16a34a)' }}>Saved.</span>}
            </div>
          )}
          {error && <div className="fax-mode-error" style={{ marginTop: 8 }}>Error: {error}</div>}
        </div>
      )}
    </div>
  );
}
